// Client-side approximation of the Go grammar in Be_Auth_DevOps
// models/boolean_search.go. It exists to catch the common mistakes before a
// round trip; the server stays the authority and its INVALID_BOOLEAN_QUERY
// details surface in the same inline slot as these messages.

const BINARY_OPERATORS = new Set(['AND', 'OR']);
const OPERATORS = new Set(['AND', 'OR', 'NOT']);

export function tokenizeBooleanQuery(query) {
  const text = String(query ?? '');
  const tokens = [];
  let i = 0;

  while (i < text.length) {
    const char = text[i];

    if (/\s/.test(char)) { i += 1; continue; }

    if (char === '(' || char === ')') {
      tokens.push({ type: char === '(' ? 'lparen' : 'rparen', value: char });
      i += 1;
      continue;
    }

    if (char === '"') {
      const close = text.indexOf('"', i + 1);
      if (close === -1) {
        tokens.push({ type: 'unterminated', value: text.slice(i) });
        break;
      }
      tokens.push({ type: 'term', value: text.slice(i + 1, close), quoted: true });
      i = close + 1;
      continue;
    }

    let end = i;
    while (end < text.length && !/[\s()"]/.test(text[end])) end += 1;
    const word = text.slice(i, end);
    const upper = word.toUpperCase();
    tokens.push(OPERATORS.has(upper) ? { type: 'operator', value: upper } : { type: 'term', value: word });
    i = end;
  }

  return tokens;
}

// Tokens people carry over from other search tools that mean something
// different (or nothing) to this grammar — flagged as a hint on the operator
// term itself, distinct from the AND/OR/NOT set the tokenizer already parses.
const FOREIGN_OPERATOR_HINTS = [
  { pattern: /&&/, message: 'Use AND instead of &&.' },
  { pattern: /\|\|/, message: 'Use OR instead of ||.' },
  { pattern: /(?:^|\s)!(?=\S)/, message: 'Use NOT instead of !.' },
];

const MAX_COMFORTABLE_DEPTH = 4;
const MAX_COMFORTABLE_TERMS = 12;

// Only these BooleanSearchRequest params are actually run through the Go
// tokenizer/parser (candidateBooleanSearchController.go — Query/Skills/
// JobTitle/Designation). Every other param (currentLocation, experience.*,
// sourceType, …) is a plain substring/exact match server-side. A field
// mapped to one of those but marked booleanCapable would send a literal
// "(hyderabad OR pune)" as search text and silently return nothing — legal
// JSON, wrong behaviour, no error anywhere. This is the allowlist that keeps
// the admin UI and the runtime component from allowing that combination.
export const BOOLEAN_PARSEABLE_API_KEYS = ['query', 'skills', 'jobTitle', 'designation'];
const PARSEABLE_SET = new Set(BOOLEAN_PARSEABLE_API_KEYS);

export function isBooleanParsable(apiKey) {
  if (Array.isArray(apiKey)) return apiKey.length === 1 && PARSEABLE_SET.has(apiKey[0]);
  return PARSEABLE_SET.has(apiKey);
}

// Two tiers, matching how the two kinds of "wrong" actually differ:
//   errors   — the grammar itself is broken (unbalanced brackets, a dangling
//              operator, "&&" instead of "AND"). The backend's own parser
//              would reject these too, so blocking here just saves the round
//              trip.
//   warnings — grammatically legal but likely not what was intended (an
//              expression that's ALL exclusions and so matches almost
//              everyone; a bare "*"/"?" the backend treats as a literal
//              character, not a wildcard, per regexp.QuoteMeta in
//              Be_Auth_DevOps/models/boolean_search.go — so it silently
//              returns nothing rather than erroring). These never block
//              Search; a query can be legal and still worth a second look.
export function validateBooleanQuery(query) {
  const errors = [];
  const warnings = [];
  const text = String(query ?? '').trim();
  if (!text) return { valid: true, errors, warnings };

  FOREIGN_OPERATOR_HINTS.forEach(({ pattern, message }) => {
    if (pattern.test(text)) errors.push(message);
  });

  const tokens = tokenizeBooleanQuery(text);

  if (tokens.some((token) => token.type === 'unterminated')) {
    errors.push('Unclosed quote — every " needs a closing ".');
  }

  let depth = 0;
  let maxDepth = 0;
  let closedTooEarly = false;
  tokens.forEach((token) => {
    if (token.type === 'lparen') {
      depth += 1;
      maxDepth = Math.max(maxDepth, depth);
    } else if (token.type === 'rparen') {
      depth -= 1;
      if (depth < 0) { closedTooEarly = true; depth = 0; }
    }
  });
  if (closedTooEarly || depth > 0) errors.push('Unbalanced parentheses.');

  const parsed = tokens.filter((token) => token.type !== 'unterminated');
  const termTokens = [];

  parsed.forEach((token, index) => {
    const prev = parsed[index - 1];
    const next = parsed[index + 1];

    if (token.type === 'operator') {
      // NOT is unary, so it legitimately opens an expression ("NOT Hibernate")
      // and legitimately follows a binary operator ("Java AND NOT Python").
      const isBinary = BINARY_OPERATORS.has(token.value);
      const missingLeft = !prev
        || prev.type === 'lparen'
        || (prev.type === 'operator' && BINARY_OPERATORS.has(prev.value));
      if (isBinary && missingLeft) errors.push(`"${token.value}" needs a term before it.`);
      if (!next || next.type === 'rparen') errors.push(`"${token.value}" needs a term after it.`);
    }

    if (token.type === 'lparen' && next?.type === 'rparen') errors.push('Empty parentheses.');

    if (token.type === 'term') {
      termTokens.push({ token, index });
      if (!token.quoted && token.value.length === 1) {
        warnings.push(`"${token.value}" is a single character and will match very broadly.`);
      }
      if (/[*?]/.test(token.value)) {
        warnings.push(`"${token.value}" — * and ? are matched as literal characters here, not wildcards.`);
      }
    }
  });

  if (errors.length === 0 && termTokens.length > 0) {
    // A term counts as "excluded" only when the nearest non-paren token
    // before it is NOT — i.e. it's actually negated, not just near one.
    const allExcluded = termTokens.every(({ index }) => {
      for (let i = index - 1; i >= 0; i -= 1) {
        const candidate = parsed[i];
        if (candidate.type === 'lparen') continue;
        return candidate.type === 'operator' && candidate.value === 'NOT';
      }
      return false;
    });
    if (allExcluded) {
      warnings.push('This query is only exclusions and will match nearly everyone.');
    }
  }

  if (maxDepth > MAX_COMFORTABLE_DEPTH) {
    warnings.push(`Nested ${maxDepth} levels deep — consider simplifying so it stays easy to verify.`);
  }
  if (termTokens.length > MAX_COMFORTABLE_TERMS) {
    warnings.push(`${termTokens.length} terms in one query — consider narrowing it.`);
  }

  return {
    valid: errors.length === 0,
    errors: [...new Set(errors)],
    warnings: [...new Set(warnings)],
  };
}

// Returns the next textarea value plus where the caret should land, so a
// toolbar click behaves like typing at the cursor rather than appending.
export function insertTokenAtCursor(value, selectionStart, selectionEnd, token) {
  const text = String(value ?? '');
  const start = Math.max(0, Math.min(selectionStart ?? text.length, text.length));
  const end = Math.max(start, Math.min(selectionEnd ?? start, text.length));

  const before = text.slice(0, start);
  const selected = text.slice(start, end);
  const after = text.slice(end);

  if (token === '()' || token === '""') {
    const [open, close] = token === '()' ? ['(', ')'] : ['"', '"'];
    return {
      value: `${before}${open}${selected}${close}${after}`,
      cursor: selected ? start + selected.length + 2 : start + 1,
    };
  }

  // Keep any selected text and place the operator after it — a toolbar click
  // should never destroy what the user highlighted.
  const head = before + selected;
  const leading = head.length > 0 && !/\s$/.test(head) ? ' ' : '';
  const trailing = !/^\s/.test(after) ? ' ' : '';
  const insert = `${leading}${token}${trailing}`;

  // When the following text already starts with a space, step over it so the
  // next keystroke begins the next term instead of gluing onto the operator.
  const skipExistingSpace = !trailing && /^\s/.test(after) ? 1 : 0;

  return {
    value: `${head}${insert}${after}`,
    cursor: head.length + insert.length + skipExistingSpace,
  };
}
